// Compact gradient-filled area curve. Reusable across tabs that need a
// small "sparkbar with style" — Pulse tiles are the primary consumer.
//
// SVG only (no recharts) — the chart is intentionally tiny and renders
// hundreds of times per page; recharts' per-instance overhead becomes
// visible at this scale. The geometry is a stroked path + a filled area
// path backed by a per-instance `<linearGradient>` (stop opacities 0.55 →
// 0.04 give the deep-fade look from the v4 mockup).
import { useId, useMemo } from 'react';

export interface AreaCurvePoint {
  t: string;
  n: number;
}

interface Props {
  data: AreaCurvePoint[];
  color: string;
  /** Tile-tile chart height. Defaults to the 72px from the v4 mockup. */
  height?: number;
  /** Whether to render the horizontal baseline. */
  showBaseline?: boolean;
  /** Optional ARIA label for screen readers (e.g. "Russia mentions over 2 years"). */
  ariaLabel?: string;
}

// Catmull-Rom → cubic-bezier smoothing. Each segment uses the tangents at
// its endpoints, capped to inside the [0,width] box so the curve never
// overshoots on the leftmost/rightmost points.
function smoothPath(points: Array<[number, number]>): string {
  if (points.length === 0) return '';
  if (points.length === 1) {
    const [x, y] = points[0];
    return `M ${x} ${y}`;
  }
  const segments: string[] = [`M ${points[0][0]} ${points[0][1]}`];
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] ?? p2;
    const t = 0.5;
    const c1x = p1[0] + (p2[0] - p0[0]) * (t / 3);
    const c1y = p1[1] + (p2[1] - p0[1]) * (t / 3);
    const c2x = p2[0] - (p3[0] - p1[0]) * (t / 3);
    const c2y = p2[1] - (p3[1] - p1[1]) * (t / 3);
    segments.push(`C ${c1x} ${c1y} ${c2x} ${c2y} ${p2[0]} ${p2[1]}`);
  }
  return segments.join(' ');
}

export default function AreaCurve({
  data,
  color,
  height = 72,
  showBaseline = true,
  ariaLabel,
}: Props) {
  const width = 320;  // intrinsic; preserveAspectRatio scales to container
  const padX = 4;
  const padY = 6;
  const gradientId = useId();

  const { linePath, areaPath, hasData } = useMemo(() => {
    if (!data || data.length === 0) {
      return { linePath: '', areaPath: '', hasData: false };
    }
    const maxN = data.reduce((acc, p) => (p.n > acc ? p.n : acc), 0) || 1;
    const innerW = width - padX * 2;
    const innerH = height - padY * 2;
    const stepX = data.length > 1 ? innerW / (data.length - 1) : 0;
    const pts: Array<[number, number]> = data.map((p, i) => {
      const x = padX + i * stepX;
      const y = padY + innerH - (p.n / maxN) * innerH;
      return [x, y];
    });
    const line = smoothPath(pts);
    const baselineY = height - padY;
    const area =
      pts.length === 1
        ? `M ${pts[0][0]} ${pts[0][1]} L ${pts[0][0]} ${baselineY} Z`
        : `${line} L ${pts[pts.length - 1][0]} ${baselineY} L ${pts[0][0]} ${baselineY} Z`;
    return { linePath: line, areaPath: area, hasData: true };
  }, [data, height]);

  if (!hasData) {
    return (
      <div
        className="trends-areacurve trends-areacurve-empty"
        style={{ height }}
        aria-label={ariaLabel}
      >
        no data
      </div>
    );
  }

  return (
    <svg
      className="trends-areacurve"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      width="100%"
      height={height}
      role="img"
      aria-label={ariaLabel}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.55} />
          <stop offset="100%" stopColor={color} stopOpacity={0.04} />
        </linearGradient>
      </defs>
      {showBaseline && (
        <line
          x1={padX}
          y1={height - padY + 0.5}
          x2={width - padX}
          y2={height - padY + 0.5}
          stroke="currentColor"
          strokeOpacity={0.15}
          strokeWidth={1}
        />
      )}
      <path d={areaPath} fill={`url(#${gradientId})`} />
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth={1.75}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
